import {ErrorBox} from '@components/commons/error-box/ErrorBox.tsx'
import {AlertTriangleIcon, CornerDownRightIcon, RefreshCw05Icon} from '@components/ui/icon/Icon.tsx'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {TableBodyRow} from '@components/ui/table-body-row/TableBodyRow.tsx'
import {TableBodyCell, TableBodyCellText} from '@components/ui/table-atoms/TableAtoms.tsx'
import {SelectColumnHeader} from '@components/ui/select-column-header/SelectColumnHeader.tsx'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {useTreatmentRequests} from '@/features/treatment-requests/services/useTreatmentRequests.ts'
import {TreatmentRequestSmall, TreatmentRequestsColumn} from '@/features/treatment-requests/types.ts'
import {Table} from '@components/commons/table/Table.tsx'
import {Link, useNavigate} from 'react-router-dom'
import {Dispatch, FC, SetStateAction} from 'react'
import {VisibilityState} from '@tanstack/react-table'
import {
    authorizationToLabel,
    makeDiseaseTreePath,
    rejectedReasonToLabel,
    treatmentRequestColumnToLabel,
    treatmentRequestStatusToLabel,
    typeToLabel,
    useTreatmentRequestSearchParams
} from '@/features/treatment-requests/utils.ts'
import {formatLocaleDate} from '@/dayjs.ts'
import {StatusBullet} from '@/features/treatment-requests/components/status-bullet/StatusBullet.tsx'
import {
    StyledDiseaseCell,
    StyledDiseaseTooltip,
    StyledSelectCell,
    StyledStatusCell,
    StyledTherapistDoctorCell
} from '@/features/treatment-requests/components/treatment-requests-table/style.ts'
import {Tooltip} from '@components/ui/tooltip/Tooltip.tsx'
import {SelectRowCheckbox} from '@components/ui/select-row-checkbox/SelectRowCheckbox.tsx'
import {
    filtersParamToPayload,
    getIsRowSelected,
    sortParamToPayload,
    workSectorToLabel,
    workServiceToLabel
} from '@utilities/helpers.ts'
import {RowWarningTooltip} from '@components/commons/row-warning-tootlip/RowWarningTooltip.tsx'
import {MoreActionsCell} from '@components/ui/more-actions-cell/MoreActionsCell.tsx'
import {RowMenu} from '@/features/treatment-requests/components/row-menu/RowMenu.tsx'
import {TratmentRequestTherapistDoctorSelector} from '@/features/treatment-requests/components/tratment-request-therapist-doctor-selector/TratmentRequestTherapistDoctorSelector'
import {routes} from '@utilities/constants/routes.tsx'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {useTheme} from 'styled-components'
import {usePermissions} from '@/features/auth/store/store'

interface TreatmentRequestsTableProps {
    status?: Exclude<TreatmentRequestSmall['status'], 'deprecated'>
    columnVisibility: VisibilityState
    isSelectAll: boolean
    setIsSelectAll: Dispatch<SetStateAction<boolean>>
    selectedTreatmentRequestsIds: string[]
    setSelectedTreatmentRequestsIds: Dispatch<SetStateAction<string[]>>
    unselectedTreatmentRequestsIds: string[]
    setUnselectedTreatmentRequestsIds: Dispatch<SetStateAction<string[]>>
    selectedTreatmentRequestsCount: number
}
export const TreatmentRequestsTable: FC<TreatmentRequestsTableProps> = ({
    status,
    columnVisibility,
    isSelectAll,
    setIsSelectAll,
    selectedTreatmentRequestsIds,
    setSelectedTreatmentRequestsIds,
    unselectedTreatmentRequestsIds,
    setUnselectedTreatmentRequestsIds,
    selectedTreatmentRequestsCount
}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const navigate = useNavigate()
    const theme = useTheme()
    const {searchParams, setSearchParams} = useTreatmentRequestSearchParams()
    const treatmentRequestsQuery = useTreatmentRequests(
        {
            limit: searchParams.limit,
            page: searchParams.page,
            search: searchParams.search,
            status
        },
        {
            filters: filtersParamToPayload(searchParams.filters)?.filters,
            sorts: sortParamToPayload(searchParams.sorts)
        }
    )

    const resetSelectionStates = () => {
        setIsSelectAll(false)
        setSelectedTreatmentRequestsIds([])
        setUnselectedTreatmentRequestsIds([])
    }

    return (
        <>
            {treatmentRequestsQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('treatment_requests:no_treatment_requests_title')}
                            subtitle={t('treatment_requests:no_treatment_requests_description')}
                        />
                    }
                    rowComponent={({tableBodyRowProps, dataItem, index}) => (
                        <TableBodyRow
                            onClick={
                                dataItem
                                    ? () =>
                                          navigate(`${routes.TREATMENT_REQUESTS_ROUTE.path}/${dataItem.id.toString()}`)
                                    : undefined
                            }
                            key={index}
                            {...tableBodyRowProps}
                        />
                    )}
                    pagination={{
                        ...treatmentRequestsQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => {
                            resetSelectionStates()
                            setSearchParams({limit, page: 1})
                        }
                    }}
                    columnPinning={{
                        left: [TreatmentRequestsColumn.enum.select, TreatmentRequestsColumn.enum.fullName],
                        right: [TreatmentRequestsColumn.enum.moreActions]
                    }}
                    columnVisibility={columnVisibility}
                    getIsRowSelected={({dataItem}) =>
                        getIsRowSelected({
                            dataItem,
                            selectedIds: selectedTreatmentRequestsIds,
                            unselectedIds: unselectedTreatmentRequestsIds,
                            isSelectAll
                        })
                    }
                    isLoading={treatmentRequestsQuery.isPending}
                    data={treatmentRequestsQuery.data?.data ?? []}
                    columns={[
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledSelectCell>
                                        {(dataItem.fieldsToCompile.includes('disease') ||
                                            dataItem.fieldsToCompile.includes('score') ||
                                            dataItem.fieldsToCompile.includes('authorization')) && (
                                            <Tooltip
                                                contentProps={{onClick: event => event.stopPropagation()}}
                                                maxWidth="320px"
                                                content={
                                                    <RowWarningTooltip
                                                        icon={RefreshCw05Icon}
                                                        title={t('treatment_requests:missing_data_title')}
                                                        paragraph={
                                                            <Trans
                                                                i18nKey="treatment_requests:missing_data_paragraph"
                                                                components={{
                                                                    cta: <Link to={dataItem.id.toString()} />
                                                                }}
                                                            />
                                                        }
                                                    />
                                                }
                                                triggerProps={{asChild: true}}
                                                variant="light"
                                            >
                                                <RefreshCw05Icon size={16} />
                                            </Tooltip>
                                        )}
                                        {permissions?.requests?.write && (
                                            <SelectRowCheckbox
                                                isSelectAll={isSelectAll}
                                                setSelectedIds={setSelectedTreatmentRequestsIds}
                                                getIsRowSelected={({dataItem}) =>
                                                    getIsRowSelected({
                                                        dataItem,
                                                        selectedIds: selectedTreatmentRequestsIds,
                                                        unselectedIds: unselectedTreatmentRequestsIds,
                                                        isSelectAll
                                                    })
                                                }
                                                dataItem={dataItem}
                                                setUnselectedIds={setUnselectedTreatmentRequestsIds}
                                            />
                                        )}
                                    </StyledSelectCell>
                                </TableBodyCell>
                            ),
                            header: () =>
                                permissions?.requests?.write ? (
                                    <SelectColumnHeader
                                        currentPageIds={treatmentRequestsQuery.data?.data.map(user => user.id) ?? []}
                                        isSelectAll={isSelectAll}
                                        selectedIds={selectedTreatmentRequestsIds}
                                        setIsSelectAll={setIsSelectAll}
                                        unselectedIds={unselectedTreatmentRequestsIds}
                                        setUnselectedIds={setUnselectedTreatmentRequestsIds}
                                        setSelectedIds={setSelectedTreatmentRequestsIds}
                                        selectedCount={selectedTreatmentRequestsCount}
                                        paginationTotal={treatmentRequestsQuery.persistedPagination.total}
                                    />
                                ) : (
                                    <></>
                                ),
                            id: TreatmentRequestsColumn.enum.select,
                            size: () => 64
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText variant="dark">{dataItem.patientFullName}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.fullName]),
                            id: TreatmentRequestsColumn.enum.fullName,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{t(workServiceToLabel[dataItem.serviceType])}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.serviceType]),
                            id: TreatmentRequestsColumn.enum.serviceType,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {dataItem.sector ? (
                                        <TableBodyCellText>{t(workSectorToLabel[dataItem.sector])}</TableBodyCellText>
                                    ) : (
                                        <TableBodyCellText variant="light">{t('commons:none')}</TableBodyCellText>
                                    )}
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.sector]),
                            id: TreatmentRequestsColumn.enum.sector,
                            minSize: () => 140,
                            size: () => 140,
                            maxSize: () => 400
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledStatusCell>
                                        <StatusBullet status={dataItem.status} />
                                        {t(treatmentRequestStatusToLabel[dataItem.status], {count: 0})}
                                    </StyledStatusCell>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.status]),
                            id: TreatmentRequestsColumn.enum.status,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{formatLocaleDate(dataItem.createdAt, 'll')}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.createdAt]),
                            id: TreatmentRequestsColumn.enum.createdAt,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.expirationDate
                                            ? formatLocaleDate(dataItem.expirationDate, 'll')
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.expirationDate]),
                            id: TreatmentRequestsColumn.enum.expirationDate,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.acceptedAt ? formatLocaleDate(dataItem.acceptedAt, 'll') : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.acceptedAt]),
                            id: TreatmentRequestsColumn.enum.acceptedAt,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.rejectedAt ? formatLocaleDate(dataItem.rejectedAt, 'll') : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.rejectedAt]),
                            id: TreatmentRequestsColumn.enum.rejectedAt,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.rejectedReason
                                            ? t(rejectedReasonToLabel[dataItem.rejectedReason])
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.rejectedReason]),
                            id: TreatmentRequestsColumn.enum.rejectedReason,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.authorization
                                            ? t(authorizationToLabel[dataItem.authorization])
                                            : t('commons:incomplete')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.authorization]),
                            id: TreatmentRequestsColumn.enum.authorization,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.authorizationDate
                                            ? formatLocaleDate(dataItem.authorizationDate, 'll')
                                            : t('commons:incomplete')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () =>
                                t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.authorizationDate]),
                            id: TreatmentRequestsColumn.enum.authorizationDate,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const consultant = dataItem.referringDoctor

                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TratmentRequestTherapistDoctorSelector
                                            treatmentRequest={dataItem}
                                            group="referring_doctor"
                                            trigger={() => (
                                                <>
                                                    {consultant === null ? (
                                                        <TableBodyCellText
                                                            variant="normal"
                                                            style={{textDecoration: 'underline'}}
                                                        >
                                                            {t('commons:none')}
                                                        </TableBodyCellText>
                                                    ) : (
                                                        <StyledTherapistDoctorCell>
                                                            <Avatar
                                                                key={consultant.id}
                                                                size={'sm'}
                                                                name={consultant.fullName}
                                                            />
                                                            <TableBodyCellText
                                                                variant="dark"
                                                                style={{textDecoration: 'underline'}}
                                                            >
                                                                {consultant.fullName}
                                                            </TableBodyCellText>
                                                        </StyledTherapistDoctorCell>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </TableBodyCell>
                                )
                            },
                            header: () =>
                                t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.referringDoctorId]),
                            id: TreatmentRequestsColumn.enum.referringDoctorId,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                const consultants = dataItem.therapists ?? []

                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <TratmentRequestTherapistDoctorSelector
                                            treatmentRequest={dataItem}
                                            group="therapist"
                                            trigger={() => (
                                                <>
                                                    {consultants.length === 0 ? (
                                                        <TableBodyCellText
                                                            variant="normal"
                                                            style={{textDecoration: 'underline'}}
                                                        >
                                                            {t('commons:none')}
                                                        </TableBodyCellText>
                                                    ) : (
                                                        <StyledTherapistDoctorCell>
                                                            {consultants.slice(0, 2).map((consultant, index) => (
                                                                <Avatar
                                                                    key={consultant.id}
                                                                    style={
                                                                        index > 0
                                                                            ? {marginLeft: -theme.spacing * 4}
                                                                            : {position: 'relative'}
                                                                    }
                                                                    size={consultants.length >= 2 ? 'xs' : 'sm'}
                                                                    name={consultant.fullName}
                                                                />
                                                            ))}
                                                            <Tooltip
                                                                rootProps={{
                                                                    open: consultants.length < 2 ? false : undefined
                                                                }}
                                                                triggerProps={{
                                                                    style: {
                                                                        width: '100%',
                                                                        overflow: 'hidden'
                                                                    }
                                                                }}
                                                                variant="light"
                                                                content={consultants.map(consultant => (
                                                                    <span key={consultant.id}>
                                                                        {consultant.fullName}
                                                                    </span>
                                                                ))}
                                                            >
                                                                <TableBodyCellText
                                                                    variant="dark"
                                                                    style={{textDecoration: 'underline'}}
                                                                >
                                                                    {consultants.length >= 2
                                                                        ? t('treatment_requests:x_therapists', {
                                                                              count: consultants.length
                                                                          })
                                                                        : consultants[0].fullName}
                                                                </TableBodyCellText>
                                                            </Tooltip>
                                                        </StyledTherapistDoctorCell>
                                                    )}
                                                </>
                                            )}
                                        />
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.therapistId]),
                            id: TreatmentRequestsColumn.enum.therapistId,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => {
                                if (dataItem.disease == null) {
                                    return (
                                        <TableBodyCell {...forwardedProps}>
                                            <TableBodyCellText>{t('commons:incomplete')}</TableBodyCellText>
                                        </TableBodyCell>
                                    )
                                }

                                const diseaseThreePath = makeDiseaseTreePath(dataItem)

                                return (
                                    <TableBodyCell {...forwardedProps}>
                                        <StyledDiseaseCell>
                                            <Tooltip
                                                variant="light"
                                                triggerProps={{asChild: true}}
                                                contentProps={{onClick: event => event.stopPropagation()}}
                                                content={
                                                    <StyledDiseaseTooltip>
                                                        {diseaseThreePath.map((name, index) => (
                                                            <span
                                                                style={{
                                                                    paddingLeft:
                                                                        index >= 2 ? (index - 1) * 8 : undefined
                                                                }}
                                                                key={index}
                                                            >
                                                                {index >= 2 && <CornerDownRightIcon />}
                                                                {name}
                                                            </span>
                                                        ))}
                                                    </StyledDiseaseTooltip>
                                                }
                                            >
                                                <TableBodyCellText>{diseaseThreePath.at(-1)}</TableBodyCellText>
                                            </Tooltip>
                                            {diseaseThreePath.length >= 2 && (
                                                <TableBodyCellText>{diseaseThreePath[0]}</TableBodyCellText>
                                            )}
                                        </StyledDiseaseCell>
                                    </TableBodyCell>
                                )
                            },
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.diagnosisId]),
                            id: TreatmentRequestsColumn.enum.diagnosisId,
                            minSize: () => 80,
                            size: () => 320,
                            maxSize: () => 500
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.type ? t(typeToLabel[dataItem.type]) : t('commons:incomplete')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.type]),
                            id: TreatmentRequestsColumn.enum.type,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.totalScore ?? t('commons:incomplete')}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.score]),
                            id: TreatmentRequestsColumn.enum.score,
                            size: () => 200
                        },
                        {
                            cell: ({forwardedProps, dataItem}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {dataItem.patientAslName
                                            ? `${dataItem.patientAslName} - ${dataItem.patientAslDistrictName}`
                                            : '-'}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () =>
                                t(treatmentRequestColumnToLabel[TreatmentRequestsColumn.enum.residenceAslDistrictId]),
                            id: TreatmentRequestsColumn.enum.residenceAslDistrictId,
                            minSize: () => 80,
                            size: () => 240,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <MoreActionsCell {...forwardedProps}>
                                    <RowMenu
                                        tableStatus={status}
                                        treatmentRequest={dataItem}
                                        onDeleteSuccess={() => {
                                            setSelectedTreatmentRequestsIds(prev =>
                                                prev.filter(id => id != dataItem.id)
                                            )
                                            setUnselectedTreatmentRequestsIds(prev =>
                                                prev.filter(id => id != dataItem.id)
                                            )
                                        }}
                                    />
                                </MoreActionsCell>
                            ),
                            header: () => undefined,
                            id: TreatmentRequestsColumn.enum.moreActions,
                            size: () => 56
                        }
                    ]}
                />
            )}
        </>
    )
}
